import { useEffect, useMemo, useState } from "react";

import {
  getImmediateInsurerLogoUrl,
  resolveInsurerLogoDownloadUrl,
} from "@/shared/utils/getInsurarLogo";

type InsurerLogoImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  insurerName?: string;
  insurerLogoFile?: string;
};

export default function InsurerLogoImage({
  insurerName,
  insurerLogoFile,
  alt,
  onError,
  ...imgProps
}: InsurerLogoImageProps) {
  const insurerInitials = useMemo(() => {
    const normalizedName = String(insurerName || "").trim();
    if (!normalizedName) return "NA";

    const parts = normalizedName.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  }, [insurerName]);

  const immediateState = useMemo(
    () => getImmediateInsurerLogoUrl(insurerName, insurerLogoFile),
    [insurerLogoFile, insurerName]
  );
  const [logoSrc, setLogoSrc] = useState<string | undefined>(immediateState.src);

  useEffect(() => {
    let isMounted = true;

    setLogoSrc(immediateState.src);

    if (!immediateState.shouldResolveAsync) {
      return () => {
        isMounted = false;
      };
    }

    const resolveLogo = async () => {
      const resolvedLogo = await resolveInsurerLogoDownloadUrl(insurerName, insurerLogoFile);
      if (isMounted) {
        setLogoSrc(resolvedLogo);
      }
    };

    void resolveLogo();

    return () => {
      isMounted = false;
    };
  }, [immediateState, insurerLogoFile, insurerName]);

  if (!logoSrc) {
    return (
      <div
        className="flex h-full w-full items-center justify-center rounded-md bg-theme-surface-muted px-2 text-[11px] font-semibold text-theme-text-soft"
        title={insurerName || "Insurer logo unavailable"}
      >
        {insurerInitials}
      </div>
    );
  }

  return (
    <img
      {...imgProps}
      src={logoSrc}
      alt={alt || insurerName || "insurer"}
      onError={(event) => {
        setLogoSrc(undefined);

        onError?.(event);
      }}
    />
  );
}
